import { AnalyticIdentifier } from '@common/neo.tax';
import { useCallback, useMemo, useState } from 'react';

import { useRdContext } from '@neo.tax/rd/context';
import { isPayrollQreWithPayrollExpense, PayrollQre } from '@neo.tax/rd/hooks/usePayrollQres';
import { FormSectionConfiguration, FormType, useAnalyticId } from '@neo.tax/shared';

import { useGetCompanyOfficerPayrollQres } from './useGetCompanyOfficerPayrollQres';
import { useSearchForPotentialCompanyOfficers } from './useSearchForPotentialCompanyOfficers';
import { useUpdateCompanyOfficer } from './useUpdateCompanyOfficer';

interface OfficerForms {
  officerForms: FormSectionConfiguration;
}

type CompanyOfficerOption = { label: string; value: string };

export const useOfficerForms = (): OfficerForms => {
  const companyOfficersAnalyticId = useAnalyticId(AnalyticIdentifier.CREDIT_CALCULATION_COMPANY_OFFICERS);
  const { fiscalYearId } = useRdContext();

  const [searchTerm, setSearchTerm] = useState<string>('');

  const { companyOfficerPayrollQres, loading: loadingCompanyOfficers } = useGetCompanyOfficerPayrollQres(fiscalYearId);
  const { loading: loadingPotentialCompanyOfficers, payrollQres: potentialCompanyOfficers } =
    useSearchForPotentialCompanyOfficers(fiscalYearId, searchTerm);

  const [updateCompanyOfficer] = useUpdateCompanyOfficer(fiscalYearId);

  const handleUpdateCompanyOfficers = useCallback(
    async (selectedOfficerOptions: CompanyOfficerOption[]): Promise<void> => {
      const officersToAdd = selectedOfficerOptions.filter(
        (selectedOfficerOption) =>
          !companyOfficerPayrollQres.map((co) => co.payrollExpenseId).includes(selectedOfficerOption.value),
      );
      const officersToRemove = companyOfficerPayrollQres
        // https://linear.app/neo-tax/issue/NEO-6221/determine-what-to-do-with-officiers-on-credit-calculation-page
        // https://linear.app/neo-tax/issue/NEO-6217/fix-check-type-issue-with-randd-changes
        // TODO: Ideally companyOfficerPayrollQres should either return a different type for just payroll expenses,
        // or the frontend should handle payroll qres with payroll expenses and contributors
        // Currently the corresponding graphql query filters out PayrollQres without payroll
        // expenses but the returned datatype makes payrollExpenseId optional
        // To resolve the type checking, we temporary filter out any payroll qres without payroll
        // expenses, which enforces the type to WithPayrollExpense<PayrollQre>
        .filter(
          (co) =>
            isPayrollQreWithPayrollExpense(co) &&
            !selectedOfficerOptions
              .map((selectedOfficerOption) => selectedOfficerOption.value)
              .includes(co.payrollExpenseId),
        );

      await Promise.all([
        ...officersToAdd.map((officer) =>
          updateCompanyOfficer({
            variables: { payrollExpenseId: officer.value, input: { isOfficer: true } },
          }),
        ),
        ...officersToRemove.map(
          (officer) =>
            isPayrollQreWithPayrollExpense(officer) &&
            updateCompanyOfficer({
              variables: { payrollExpenseId: officer.payrollExpenseId, input: { isOfficer: false } },
            }),
        ),
      ]);
    },
    [companyOfficerPayrollQres, updateCompanyOfficer],
  );

  const officerToOption = (officer: PayrollQre): CompanyOfficerOption => ({
    label: officer.title ? `${officer.name} (${officer.title})` : officer.name,
    // https://linear.app/neo-tax/issue/NEO-6221/determine-what-to-do-with-officiers-on-credit-calculation-page
    // https://linear.app/neo-tax/issue/NEO-6217/fix-check-type-issue-with-randd-changes
    // TODO: Ideally companyOfficerPayrollQres should either return a different type for just payroll expenses,
    // or the frontend should handle payroll qres with payroll expenses and contributors
    // Currently the corresponding graphql query filters out PayrollQres without payroll
    // expenses but the returned datatype makes payrollExpenseId optional
    // To resolve the type checking, we temporary filter out any payroll qres without payroll
    // expenses, which enforces the type to WithPayrollExpense<PayrollQre>
    value: isPayrollQreWithPayrollExpense(officer) ? officer.payrollExpenseId : '',
  });

  const officerForms: FormSectionConfiguration = useMemo(
    () => ({
      title: 'Company Officers',
      subtitle: "Mark all of the company's officers to properly report any officer wage QREs on Form 6765 line 42.",
      forms: [
        {
          title: 'Who were the company officers?',
          subtitle:
            "Officers are determined by the laws of the state where the company is incorporated and the company's bylaws. This usually includes the CEO and may include other executives if designated as officers by the company and its bylaws.",
          formType: FormType.AUTOCOMPLETE_TAG,
          key: 'companyOfficers',
          id: companyOfficersAnalyticId,
          options: companyOfficerPayrollQres.concat(potentialCompanyOfficers).map(officerToOption),
          values: companyOfficerPayrollQres.map(officerToOption),
          onChange: (value): Promise<void> => handleUpdateCompanyOfficers(value as CompanyOfficerOption[]),
          placeholder: 'Search by name or title',
          allowCreate: false,
          allowRemove: true,
          isLoading: loadingCompanyOfficers || loadingPotentialCompanyOfficers,
          searchKeyword: searchTerm,
          searchKeywordDebounce: 0,
          setSearchKeyword: (searchKeyword: string | null) => setSearchTerm(searchKeyword ?? ''),
        },
      ],
    }),
    [
      companyOfficerPayrollQres,
      companyOfficersAnalyticId,
      handleUpdateCompanyOfficers,
      loadingCompanyOfficers,
      loadingPotentialCompanyOfficers,
      potentialCompanyOfficers,
      searchTerm,
    ],
  );

  return {
    officerForms,
  };
};
